import { LangSync } from "@/components/LangSync";
import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider, useLang } from "@/context/LanguageContext";
import AuthCallback from "@/pages/AuthCallback";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Notifications from "@/pages/Notifications";
import { NotificationWatcher } from "@/lib/notifications";
import { ChatWatcher } from "@/lib/chat";
import ListingDetail from "@/pages/ListingDetail";
import MyAds from "@/pages/MyAds";
import MyBusiness from "@/pages/MyBusiness";
import Wallet from "@/pages/Wallet";
import Account from "@/pages/Account";
import Categories from "@/pages/Categories";
import Favorites from "@/pages/Favorites";
import CategoryPage from "@/pages/CategoryPage";
import CategoryAds from "@/pages/CategoryAds";
import AssignedSupport from "@/pages/AssignedSupport";
import SupportAssign from "@/pages/SupportAssign";
import LegalDoc from "@/pages/LegalDoc";
import { api } from "@/lib/api";

const PREFETCH = [];
const lazyPage = (load) => { PREFETCH.push(load); return lazy(load); };
const Register = lazyPage(() => import("@/pages/Register"));
const Bookings = lazyPage(() => import("@/pages/Bookings"));
const ForgotPassword = lazyPage(() => import("@/pages/ForgotPassword"));
const VerifyEmail = lazyPage(() => import("@/pages/VerifyEmail"));
const Messages = lazyPage(() => import("@/pages/Messages"));
const PostAd = lazyPage(() => import("@/pages/PostAd"));
const AdminDashboard = lazyPage(() => import("@/pages/AdminDashboard"));
const EditProfile = lazyPage(() => import("@/pages/EditProfile"));
const PaymentReceipts = lazyPage(() => import("@/pages/PaymentReceipts"));
const SimplePage = lazyPage(() => import("@/pages/SimplePage"));
const PaymentResult = lazyPage(() => import("@/pages/PaymentResult"));
const Appointments = lazyPage(() => import("@/pages/Appointments"));
const ChangePassword = lazyPage(() => import("@/pages/ChangePassword"));
const ActiveDevices = lazyPage(() => import("@/pages/ActiveDevices"));
const PaymentHistory = lazyPage(() => import("@/pages/PaymentHistory"));
const SearchPage = lazyPage(() => import("@/pages/Search"));
const SavedSearches = lazyPage(() => import("@/pages/SavedSearches"));
const Compare = lazyPage(() => import("@/pages/Compare"));
const Seller = lazyPage(() => import("@/pages/Seller"));
const Center = lazyPage(() => import("@/pages/Center"));
const PhoneRequired = lazyPage(() => import("@/pages/PhoneRequired"));
const CustomerService = lazyPage(() => import("@/pages/CustomerService"));
const SupportLive = lazyPage(() => import("@/pages/SupportLive"));
const SupportAI = lazyPage(() => import("@/pages/SupportAI"));

function PageSpinner() {
  return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
}

function usePrefetchPages() {
  useEffect(() => {
    const run = () => PREFETCH.forEach((load) => load().catch(() => {}));
    const id = window.requestIdleCallback ? window.requestIdleCallback(run, { timeout: 3000 }) : setTimeout(run, 1500);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);
}

function Protected({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !["admin", "moderator"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  usePrefetchPages();
  const location = useLocation();
  const { user, setUser } = useAuth();
  // Fresh account data on page change (throttled; only re-render if something changed).
  useEffect(() => {
    if (!user || Date.now() - (window.__meAt || 0) < 15000) return;
    window.__meAt = Date.now();
    api.get("/auth/me").then(({ data }) => { if (JSON.stringify(data) !== JSON.stringify(user)) setUser(data); }).catch(() => {});
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (user?.phone_required && !["/add-phone", "/terms", "/privacy"].includes(location.pathname)) return <Navigate to="/add-phone" replace />;
  return (
    <>
      <Suspense fallback={<PageSpinner />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/complete-signup" element={<PhoneRequired mode="signup" />} />
        <Route path="/add-phone" element={<Protected><PhoneRequired mode="existing" /></Protected>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/category/:catId" element={<CategoryPage />} />
        <Route path="/category/:catId/ads" element={<CategoryAds />} />
        <Route path="/category/:catId/:subId" element={<CategoryPage />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/seller/:id" element={<Seller />} />
        <Route path="/center/:id" element={<Center />} />
        <Route path="/compare" element={<Protected><Compare /></Protected>} />
        <Route path="/post" element={<Protected><PostAd /></Protected>} />
        <Route path="/my-ads" element={<Protected><MyAds /></Protected>} />
        <Route path="/edit-ad/:id" element={<Protected><PostAd /></Protected>} />
        <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
        <Route path="/my-business" element={<Protected><MyBusiness /></Protected>} />
        <Route path="/account" element={<Protected><Account /></Protected>} />
        <Route path="/support-assign" element={<Protected><SupportAssign /></Protected>} />
        <Route path="/assigned-support" element={<Protected><AssignedSupport /></Protected>} />
        <Route path="/assigned-support/:chatId" element={<Protected><AssignedSupport /></Protected>} />
        <Route path="/favorites" element={<Protected><Favorites /></Protected>} />
        <Route path="/edit-profile" element={<Protected><EditProfile /></Protected>} />
        <Route path="/payment-receipts" element={<Protected><PaymentReceipts /></Protected>} />
        <Route path="/payment/success" element={<Protected><PaymentResult /></Protected>} />
        <Route path="/payment/cancel" element={<Protected><PaymentResult cancelled /></Protected>} />
        <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
        <Route path="/messages" element={<Protected><Messages /></Protected>} />
        <Route path="/messages/:convId" element={<Protected><Messages /></Protected>} />
        <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
        <Route path="/bookings" element={<Protected><Bookings /></Protected>} />
        <Route path="/appointments" element={<Protected><Appointments /></Protected>} />
        <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />
        <Route path="/payment-history" element={<Protected><PaymentHistory /></Protected>} />
        <Route path="/saved-searches" element={<Protected><SavedSearches /></Protected>} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/active-devices" element={<Protected><ActiveDevices /></Protected>} />
        <Route path="/customer-service" element={<CustomerService />} />
        <Route path="/support/live" element={<Protected><SupportLive /></Protected>} />
        <Route path="/support/ai" element={<Protected><SupportAI /></Protected>} />
        <Route path="/terms" element={<LegalDoc kind="terms" />} />
        <Route path="/privacy" element={<LegalDoc kind="privacy" />} />
        <Route path="/legal/terms" element={<LegalDoc kind="terms" />} />
        <Route path="/legal/privacy" element={<LegalDoc kind="privacy" />} />
        <Route path="/payments" element={<Protected><PaymentHistory /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

function ToasterRtl() {
  const { dir } = useLang();
  return <Toaster position="top-center" richColors dir={dir} />;
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <ToasterRtl />
            <NotificationWatcher />
            <LangSync />
            <ChatWatcher />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
